// ContactsPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Plus, X, Download, Info, Phone, Mail, RefreshCw } from 'lucide-react';

const API_BASE =
  (typeof process !== 'undefined' && process.env?.REACT_APP_AGENT_API_URL) ||
  (typeof process !== 'undefined' && process.env?.REACT_APP_AUTH_API_URL?.replace('/api', '')) ||
  '';

const CONTACTS_ENDPOINT = `${API_BASE}/api/contacts`;

function clsx(...a) { return a.filter(Boolean).join(' '); }
const Pill = ({ children, className = '' }) => <span className={clsx('inline-flex items-center px-2 py-0.5 rounded text-xs', className)}>{children}</span>;
const formatDate = (v) => v ? new Date(v).toLocaleString() : '—';

export default function ContactsPage() {
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', details: '' });
  const hasMore = useMemo(() => page * limit < total, [page, limit, total]);

  async function fetchJson(url, opts) {
    const res = await fetch(url, { mode: 'cors', ...(opts || {}) });
    const ct = res.headers.get('content-type') || '';
    const text = await res.text();
    let json = null; try { json = text ? JSON.parse(text) : null } catch { }
    if (!res.ok || json?.success === false) {
      const msg = json?.message || text || `${res.status} ${res.statusText}`;
      throw new Error(msg);
    }
    return json ?? {};
  }

  async function load() {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (q) params.set('q', q);
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const url = `${CONTACTS_ENDPOINT}?${params.toString()}`;
      const json = await fetchJson(url);
      setRows(json.entries || []);
      setTotal(json.total || 0);
    } catch (e) { setError(e.message || 'Failed to load'); setRows([]); setTotal(0); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, from, to, page, limit]);

  async function createContact(e) {
    e?.preventDefault?.();
    try {
      const body = JSON.stringify(form);
      await fetchJson(CONTACTS_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body });
      setShowModal(false);
      setForm({ firstName: '', lastName: '', email: '', phone: '', details: '' });
      setPage(1);
      load();
    } catch (err) { alert(`Create failed: ${err.message}`); }
  }

  function exportCSV() {
    const cols = ['First', 'Last', 'Phone', 'Email', 'Details', 'CreatedAt'];
    const lines = rows.map(r => [
      r.firstName || '', r.lastName || '', r.phone || '', r.email || '', (r.details || '').replace(/\n/g, ' '), r.createdAt ? new Date(r.createdAt).toISOString() : ''
    ]);
    const csv = [cols.join(','), ...lines.map(r => r.map(x => `"${String(x).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `contacts_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="min-h-dvh flex flex-col bg-slate-50 text-slate-900 [scrollbar-gutter:stable]">
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-slate-200">
        <div className="h-16 w-full px-4 flex items-center justify-between">
          <div className="text-lg md:text-2xl font-semibold">Main CRM (Contacts)</div>
          <div className="flex items-center gap-2">
            <button onClick={exportCSV} className="rounded-xl border border-slate-200 px-3 py-2 text-sm hover:bg-slate-100"><Download className="w-4 h-4 inline mr-2" />Export</button>
            <button onClick={() => setShowModal(true)} className="rounded-xl bg-slate-900 text-white px-3 py-2 text-sm hover:bg-slate-800"><Plus className="w-4 h-4 inline mr-2" />Add Contact</button>
          </div>
        </div>
      </header>

      <div className="w-full px-4 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          <div className="lg:col-span-4">
            <input className="w-full pl-3 pr-3 py-2.5 rounded-xl border border-slate-300 bg-white" placeholder="Search name, email, phone…" value={q} onChange={e => { setQ(e.target.value); setPage(1); }} />
          </div>
          <div className="lg:col-span-2 flex items-center gap-2">
            <input type="date" className="w-full py-2.5 px-3 rounded-xl border border-slate-300 bg-white" value={from} onChange={e => { setFrom(e.target.value); setPage(1); }} />
            <span className="text-slate-500">—</span>
            <input type="date" className="w-full py-2.5 px-3 rounded-xl border border-slate-300 bg-white" value={to} onChange={e => { setTo(e.target.value); setPage(1); }} />
          </div>
          <div className="lg:col-span-2">
            <button onClick={() => load()} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-white border border-slate-200 px-3 py-2.5 text-sm hover:bg-slate-50">
              <RefreshCw className={clsx('w-4 h-4', loading && 'animate-spin')} /> Refresh
            </button>
          </div>
        </div>
      </div>

      <main className="w-full px-4 pb-8">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left font-semibold px-4 py-3">First</th>
                  <th className="text-left font-semibold px-4 py-3">Last</th>
                  <th className="text-left font-semibold px-4 py-3">Phone</th>
                  <th className="text-left font-semibold px-4 py-3">Email</th>
                  <th className="text-left font-semibold px-4 py-3">Details</th>
                  <th className="text-left font-semibold px-4 py-3">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {loading && [...Array(8)].map((_, i) => (
                  <tr key={`sk-${i}`} className="animate-pulse">
                    <td className="px-4 py-4"><div className="h-4 w-28 bg-slate-200 rounded" /></td>
                    <td className="px-4 py-4"><div className="h-4 w-28 bg-slate-200 rounded" /></td>
                    <td className="px-4 py-4"><div className="h-4 w-28 bg-slate-200 rounded" /></td>
                    <td className="px-4 py-4"><div className="h-4 w-36 bg-slate-200 rounded" /></td>
                    <td className="px-4 py-4"><div className="h-4 w-64 bg-slate-200 rounded" /></td>
                    <td className="px-4 py-4"><div className="h-4 w-32 bg-slate-200 rounded" /></td>
                  </tr>
                ))}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-16 text-center text-slate-500">
                    <div className="flex flex-col items-center gap-2"><Info className="w-6 h-6" /><div className="font-medium">No contacts found</div></div>
                  </td></tr>
                )}
                {!loading && rows.map(r => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-4">{r.firstName || '—'}</td>
                    <td className="px-4 py-4">{r.lastName || '—'}</td>
                    <td className="px-4 py-4">{r.phone ? (<span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{r.phone}</span>) : '—'}</td>
                    <td className="px-4 py-4">{r.email ? (<span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{r.email}</span>) : '—'}</td>
                    <td className="px-4 py-4 text-slate-700 max-w-[36rem]">{r.details || '—'}</td>
                    <td className="px-4 py-4">{formatDate(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-white">
            <div className="text-sm text-slate-600">
              Showing <span className="font-medium">{rows.length}</span> of <span className="font-medium">{total}</span>
            </div>
            <div className="flex items-center gap-4">
              <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-sm">
                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
              </select>
              <div className="flex items-center gap-2">
                <button className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm disabled:opacity-50 hover:bg-slate-50" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Prev</button>
                <div className="text-sm">Page <span className="font-semibold">{page}</span></div>
                <button className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm disabled:opacity-50 hover:bg-slate-50" onClick={() => setPage(p => (hasMore ? p + 1 : p))} disabled={!hasMore}>Next</button>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-800 px-4 py-3">{error}</div>}
      </main>

      {/* Add Contact Modal */}
      {showModal && (
        <div className="fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowModal(false)} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <form onSubmit={createContact} className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-lg font-semibold">Add Contact</div>
                <button type="button" onClick={() => setShowModal(false)} className="rounded-lg p-2 hover:bg-slate-100" aria-label="Close"><X className="w-5 h-5" /></button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input className="border rounded-xl px-3 py-2" placeholder="First name" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
                <input className="border rounded-xl px-3 py-2" placeholder="Last name" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
                <input className="border rounded-xl px-3 py-2 md:col-span-2" placeholder="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                <input className="border rounded-xl px-3 py-2 md:col-span-2" placeholder="Phone (+1…)" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                <textarea className="border rounded-xl px-3 py-2 md:col-span-2" rows={4} placeholder="Details (optional)" value={form.details} onChange={e => setForm(f => ({ ...f, details: e.target.value }))} />
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button type="button" onClick={() => setShowModal(false)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm hover:bg-slate-100">Cancel</button>
                <button type="submit" className="rounded-xl bg-slate-900 text-white px-3 py-2 text-sm hover:bg-slate-800">Add Contact</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
